import React, { Suspense, useState } from "react";
import "./App.css";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import Providers from "./pages/providers";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./core/theme";
import AdminPage from "./pages/admin/admin-page";
import AuthContext from "./core/context/auth-context";
import OverviewPage from "./pages/overview";
import DHBackdrop from "./components/generic/dh-backdrop";
import AuthPage from "./pages/auth/auth-page";
import ConsoleChoice from "./pages/console/console-choice";
import { isSessionValid } from "./core/service/session";

// import Login from "./components/auth/login";
// import SmsVerification from "./components/auth/sms-verification";
import { IAppDetails } from "./core/interface/general/IGeneral";
import { SnackbarProvider } from "notistack";
import PasswordlessInitiation from "./components/passwordless/initiation";
import EmailVerification from "./components/passwordless/email-verify";
import SmsVerifyComponent from "./components/passwordless/sms-verify";

// Lazy-loaded so the FHIR Server dashboard's deps don't bloat the login/providers path.
const FhirServerPage = React.lazy(() => import("./pages/fhir-server/fhir-server-page"));

// Re-exported for any consumer that imported the theme from App historically.
export { theme };

function App() {
  const [appState, setAppState] = useState<IAppDetails>({} as IAppDetails);
  return (
    <AuthContext.Provider
      value={{ appContext: appState, setAppContext: setAppState }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider
          maxSnack={3}
          autoHideDuration={3000}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Routes>
            <Route element={<AuthPage />} path="/">
              {/* <Route index element={<Login />} />
              <Route path="sms" element={<SmsVerification />} /> */}
              <Route index element={<PasswordlessInitiation />} />
              <Route path="email-verify" element={<EmailVerification />} />
              <Route path="sms-verify" element={<SmsVerifyComponent />} />
              {/* Post-auth landing: choose the FHIR admin console or provider selection.
                  Rendered without the app chrome so no gated nav can 401 before a
                  provider-scoped adminToken exists. */}
              <Route path="console" element={<ConsoleChoice />} />
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<AdminPage />} path="/admin">
                <Route element={<Providers />} path="providers" />
                <Route element={<OverviewPage />} path="overview" />
                <Route
                  element={
                    <Suspense fallback={<DHBackdrop />}>
                      <FhirServerPage />
                    </Suspense>
                  }
                  path="fhir-server"
                />
              </Route>
            </Route>
          </Routes>
        </SnackbarProvider>
      </ThemeProvider>
    </AuthContext.Provider>
  );
}

// Gate the admin area on a live Cognito session. An expired/absent idToken (e.g. after the ~1h
// lifetime) sends the user to login instead of rendering a dashboard that would 4xx on its next call.
const ProtectedRoute = ({ redirectPath = "/" }) => {
  return isSessionValid() ? <Outlet /> : <Navigate to={redirectPath} replace />;
};

export default App;
